import { MagicLink } from 'state/magicLink';
import log from 'loglevel';
import { useCallback, useMemo } from 'react';
import { MagicLinkVerifyRequest } from 'hooks/requests/magicLinkVerify';
import useHandleAcceptedMagicLinkRequest from 'hooks/requests/magicLinkVerify';
import { useSnackbar } from 'notistack';
import useConsumerSearchWithNavigation from './useConsumerSearchWithNavigation';
import useWorkflow, { Step } from 'hooks/useWorkflow';

const useHandleAcceptedMagicLink = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [magicLinkVerifyRequest] = useHandleAcceptedMagicLinkRequest();
  const searchConsumerAndNavigate = useConsumerSearchWithNavigation();

  const { pathAfter } = useWorkflow();
  const nextPath = useMemo(() => pathAfter(Step.MAGIC_LINK_VERIFICATION), [
    pathAfter,
  ]);

  const verifyMagicLink = useCallback(
    async (magicLink: MagicLink, otpCode?: string) => {
      if (!magicLink || !magicLink.magicLinkId) {
        throw new Error('magicLinkId or otpCode is missing');
      }

      const params: MagicLinkVerifyRequest = {
        magicLinkId: magicLink.magicLinkId,
        otpCode: otpCode,
      };

      return await magicLinkVerifyRequest(params);
    },
    [magicLinkVerifyRequest],
  );

  const searchConsumer = useCallback(
    async (mobilePhone: string, ssn: string, zip: string, otpCode?: string) => {
      try {
        return await searchConsumerAndNavigate(
          {
            otpCode,
            mobilePhone,
            ssn,
            zip,
          },
          nextPath,
        );
      } catch (e) {
        log.error(e);
        enqueueSnackbar('Something has gone wrong. Please try again later', {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, nextPath, searchConsumerAndNavigate],
  );

  const handleAcceptedMagicLink = useCallback(
    async (
      magicLink: MagicLink,
      mobilePhone: string,
      ssn: string,
      zip: string,
      otpCode?: string,
    ) => {
      await verifyMagicLink(magicLink, otpCode);
      await searchConsumer(mobilePhone, ssn, zip, otpCode);
    },
    [searchConsumer, verifyMagicLink],
  );

  return handleAcceptedMagicLink;
};

export default useHandleAcceptedMagicLink;
